import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Persistent left-nav for the admin section — replaces what used to be a
// dropdown menu off TopBanner's own "Admin" button. Every admin page
// renders this with its own `active` key so switching sections feels like
// one persistent admin area instead of separate, disconnected pages (same
// idea as AdminUsers.jsx's own DirectoryTabs, just vertical and spanning
// the whole admin section instead of just the Directory/Groups pair).
const ITEMS = [
  { key: 'sso-stats', label: 'SSO Statistics', icon: '📈', path: '/admin/sso-stats', requires: 'superAdmin' },
  { key: 'hierarchy', label: 'Manage Hierarchy', icon: '⚙️', path: '/admin/hierarchy', requires: 'superAdmin' },
  { key: 'approvals', label: 'User creation Approvals', icon: '✅', path: '/admin/approvals', requires: 'superAdmin' },
  { key: 'bulk-users', label: 'Bulk User Onboarding', icon: '📤', path: '/admin/bulk-users', requires: 'superAdmin' },
  { key: 'users', label: 'User Directory', icon: '👤', path: '/admin/users', requires: 'superAdmin' },
  { key: 'apps', label: 'Application Management', icon: '🧩', path: '/admin/apps', requires: 'superAdmin' },
  { key: 'app-provisioning', label: 'App Provisioning', icon: '🛠️', path: '/admin/app-provisioning', requires: 'superAdmin' },
  { key: 'user-activity', label: 'User Activity', icon: '🕒', path: '/admin/user-activity', requires: 'superAdmin' },
  { key: 'audit-log', label: 'Audit Log', icon: '📜', path: '/admin/audit-log', requires: 'superAdmin' },
  { key: 'active-sessions', label: 'Active Sessions', icon: '🟢', path: '/admin/active-sessions', requires: 'superAdmin' },
  { key: 'app-usage', label: 'App Usage', icon: '📊', path: '/admin/app-usage', requires: 'superAdmin' },
];

const panel = {
  boxSizing: 'border-box',
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '16px 12px',
  width: '240px',
  flexShrink: 0,
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
  // The row this sits in (see each admin page) is stretched to fill
  // `page`'s own fixed height, so 100% here makes this panel exactly as
  // tall as the content card beside it — matching heights instead of
  // sitting short at its own natural content height with a gap below it.
  // `overflowY: auto` is a safety net for if this list ever grows past
  // what fits, not something that normally engages.
  height: '100%',
  overflowY: 'auto',
};

function navBtnStyle(isActive) {
  return {
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
    textAlign: 'left', background: isActive ? '#EAF6FD' : 'transparent',
    border: 'none', borderLeft: isActive ? '3px solid #009FE3' : '3px solid transparent',
    boxSizing: 'border-box',
    color: isActive ? '#0072B1' : '#3a3a3a', textDecoration: 'none',
    padding: '11px 12px', borderRadius: '8px',
    fontSize: '14px', fontWeight: isActive ? '700' : '500', cursor: 'pointer',
    marginBottom: '4px',
  };
}

// `active` is a sidebar item key, not a route — AdminUsers.jsx and
// AdminGroups.jsx (Directory/Groups tabs) both pass "users" so the sidebar
// stays on "User Directory" while that pair's own tab bar handles
// switching between them.
export default function AdminSidebar({ active }) {
  const { user } = useAuth();
  const userGroups = user?.profile?.groups || [];
  // all-apps-admin's admin-section access was revoked 2026-09-10 — every
  // item here now requires bescom-super-admins (see adminUsers.js's own
  // comment); 'users' was the last one that used to accept the lower tier.
  const isSuperAdmin = userGroups.includes('bescom-super-admins');

  const visibleItems = ITEMS.filter(() => isSuperAdmin);

  return (
    <div className="admin-sidebar-panel" style={panel}>
      {/* Every admin page (AdminUsers.jsx, AdminHierarchy.jsx, etc.) renders
          this component and shares its own `page`/row wrapper — rather than
          repeat this same media query in all nine of those files, it's
          defined once here since this component is common to every one of
          them. Below 900px, the fixed 240px-sidebar-beside-a-card layout
          has nowhere near enough room (a phone screen minus the sidebar
          leaves under 100px for actual content) — this switches the whole
          row to a vertical stack (.admin-row) with the sidebar itself
          becoming a horizontal, scrollable pill strip above the content
          instead of a tall vertical list eating half the screen.
          .admin-page-wrap's height/overflow are relaxed too, since a
          stacked layout needs the page to scroll normally instead of the
          strict fixed-height + internal-scroll-only design the desktop
          two-column layout relies on. */}
      <style>{`
        @media (max-width: 900px) {
          .admin-page-wrap {
            height: auto !important;
            min-height: calc(100vh - 88px);
            overflow: visible !important;
            padding: 12px !important;
          }
          .admin-row {
            flex-direction: column !important;
            height: auto !important;
          }
          .admin-sidebar-panel {
            width: 100% !important;
            height: auto !important;
            display: flex !important;
            flex-direction: row !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            gap: 6px;
            padding: 10px !important;
          }
          .admin-sidebar-panel .admin-sidebar-heading { display: none; }
          .admin-sidebar-btn {
            width: auto !important;
            white-space: nowrap;
            margin-bottom: 0 !important;
            flex-shrink: 0;
          }
        }
      `}</style>
      <p className="admin-sidebar-heading" style={{ margin: '4px 8px 12px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', color: '#888', textTransform: 'uppercase' }}>
        Admin
      </p>
      {visibleItems.map((item) => (
        <Link
          key={item.key}
          to={item.path}
          className="admin-sidebar-btn"
          style={navBtnStyle(active === item.key)}
          onMouseOver={(e) => { if (active !== item.key) e.currentTarget.style.background = '#F0F4F8'; }}
          onMouseOut={(e) => { if (active !== item.key) e.currentTarget.style.background = 'transparent'; }}
        >
          <span>{item.icon}</span>
          {item.label}
        </Link>
      ))}
    </div>
  );
}
